<template>
  <div v-if="purchaseUrl && !showEmailInput && status !== 'success'">
    <ActionButton
      :text="subscribeButtonText ?? ''"
      color="blue"
      :href="purchaseUrl"
    />
  </div>

  <div
    v-if="!emailAddress || status === 'success'"
    class="magic-link-container"
  >
    <div v-if="status === 'success'" class="magic-link-success-message">
      {{ t("web.subscription.message.magic_link_sent") }}
    </div>

    <div v-else-if="status === 'error'" class="magic-link-error-message">
      {{ message }}
    </div>

    <div v-else class="magic-link-form">
      <label v-if="showEmailInput" for="magic-link-email" class="sr-only">
        {{ t("web.form.label.email_for_sign_in") }}
      </label>
      <input
        v-if="showEmailInput"
        id="magic-link-email"
        ref="emailInput"
        v-model="localEmailAddress"
        type="email"
        :placeholder="t('web.form.placeholder.email_address')"
        class="magic-link-input"
        :disabled="status === 'pending'"
        @keydown.enter="handleKeydown"
      />

      <CaptchaChallenge
        v-if="showEmailInput && captchaRequired"
        :newsletter="newsletter"
        @callback="handleCaptchaToken"
        @expired-callback="handleCaptchaExpired"
        @error-callback="onCaptchaError"
      />

      <ActionButton
        v-if="showEmailInput && !captchaRequired"
        :text="
          status === 'pending'
            ? t('web.subscription.action.sending_sign_in_link')
            : t('web.subscription.action.send_sign_in_link')
        "
        color="blue"
        :disabled="status === 'pending' || !localEmailAddress"
        :pending="status === 'pending'"
        class="magic-link-button"
        @click="handleClick"
      />

      <button
        v-else-if="!showEmailInput"
        type="button"
        class="magic-link-toggle-button"
        @click="handleClick"
      >
        <div class="magic-link-toggle-text">
          {{ t("web.subscription.message.already_paid_subscriber") }}
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef, watch } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "../../lib/utils";
import { useMagicLink } from "../composables/useMagicLink";
import ActionButton from "./ActionButton.vue";
import CaptchaChallenge from "./CaptchaChallenge.vue";

const props = defineProps<{
  newsletter: Newsletter;
  emailAddress: string;
  purchaseUrl?: string;
  subscribeButtonText?: string;
}>();

const t = createTranslator(() => props.newsletter.locale);

const showEmailInput = ref(false);
const localEmailAddress = ref("");
const emailInput = useTemplateRef("emailInput");

const {
  status,
  message,
  captchaRequired,
  send,
  setCaptchaToken,
  handleCaptchaError,
  reset,
} = useMagicLink();

function handleClick() {
  if (!showEmailInput.value) {
    showEmailInput.value = true;
    return;
  }

  send(localEmailAddress.value);
}

function handleCaptchaToken(token: string) {
  setCaptchaToken(token);
  send(localEmailAddress.value);
}

function handleCaptchaExpired() {
  setCaptchaToken("");
}

function onCaptchaError() {
  handleCaptchaError(t("web.subscription.error.captcha_failed"));
}

function handleKeydown(event: KeyboardEvent) {
  event.preventDefault();
  send(localEmailAddress.value);
}

watch(showEmailInput, async (visible) => {
  if (visible) {
    await nextTick();
    emailInput.value?.focus();
  }
});

watch(
  () => props.emailAddress,
  (email) => {
    if (email && status.value !== "success") {
      showEmailInput.value = false;
      reset();
    }
  }
);
</script>
